import * as React from 'react';
import ScrollPanel from 'app/shared/ui/ScrollPanel';
import { Modal, Button, Checkbox, Collapse } from 'antd';
import { SchoolsByCountry } from 'app/pages/AgentPricing/modules/RefData/state';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action, ChangeSchoolOverrideSpec } from 'app/pages/AgentPricing/actionTypes';
import { getSchoolsByCountry } from 'app/pages/AgentPricing/modules/RefData';
import { getSchoolOverrides } from 'app/pages/AgentPricing/modules/RatesTableWidget/selectors';
import { getCurrentDialog, CurrentAgentCommissionDialog } from 'app/pages/AgentPricing/modules/EditDialogs';
import { changeCurrentDialog, changeSchoolOverrides } from 'app/pages/AgentPricing/actions';
import { CaretRightOutlined, CaretDownOutlined } from '@ant-design/icons';
import {getSchoolKey} from "app/pages/AgentPricing/utils/schoolIds";

const { Panel } = Collapse;

interface SchoolOverrideDialogProps {}

interface StateProps {
    schoolsByCountry: SchoolsByCountry[]
    currentSchoolOverrides: string[]
    isDialogOpen: boolean
}

interface DispatchProps {
    closeDialog: () => void
    save: (schoolOverrides: ChangeSchoolOverrideSpec[]) => void
}

interface InternalState {
    openCountries: {[code: string]: boolean}
    selectedSchools: {[code: string]: boolean}
}

type InternalProps = StateProps&DispatchProps;

class SchoolOverrideDialog extends React.Component<InternalProps, InternalState> {
    constructor(props: InternalProps) {
        super(props);

        this.state = {
            openCountries: {},
            selectedSchools: {}
        };
        props.schoolsByCountry.forEach(c => 
            this.state.openCountries[c.countryCode] = false
        );

        props.currentSchoolOverrides.forEach(s =>
            this.state.selectedSchools[s] = true
        );
    }

    render() {
        return (
            <Modal
                title="Add/Remove Schools"
                open={this.props.isDialogOpen}
                onCancel={this.onCloseDialog.bind(this)}
                footer={[
                    <Button key="cancel" onClick={this.onCloseDialog.bind(this)}>
                        Cancel
                    </Button>,
                    <Button 
                        key="save" 
                        type="primary" 
                        onClick={this.save.bind(this)}
                    >
                        Save
                    </Button>
                ]}
                width={600}
            >
                <ScrollPanel height="400px">
                    <Collapse 
                        expandIcon={({ isActive }) => 
                            isActive ? <CaretDownOutlined /> : <CaretRightOutlined />
                        }
                    >
                        {this.props.schoolsByCountry.map(country => (
                            <Panel 
                                key={country.countryCode}
                                header={
                                    <span>
                                        <img 
                                            src={`https://flagcdn.com/16x12/${country.countryCode.toLowerCase()}.png`}
                                            alt={country.countryName}
                                            style={{ marginRight: 8 }}
                                        />
                                        {country.countryName}
                                    </span>
                                }
                            >
                                <div style={{ marginLeft: 24 }}>
                                    {country.schools.map(school => {
                                        const schoolKey = getSchoolKey(school);

                                        return (
                                            <div key={schoolKey} style={{ marginBottom: 8 }}>
                                                <Checkbox
                                                    checked={this.isSelected(schoolKey)}
                                                    onChange={() => this.changeSelected(schoolKey)}
                                                >
                                                    {school.name}
                                                </Checkbox>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Panel>
                        ))}
                    </Collapse>
                </ScrollPanel>
            </Modal>
        );
    }

    componentDidUpdate(prevProps: InternalProps) {
        if (this.props.isDialogOpen && !prevProps.isDialogOpen) {
            this.onOpenDialog();
        }
    }

    private onOpenDialog() {
        const newState : InternalState = {
            selectedSchools: {},
            openCountries: {}

        };
        this.props.schoolsByCountry.forEach(c => 
            newState.openCountries[c.countryCode] = false
        );

        this.props.currentSchoolOverrides.forEach(s =>
            newState.selectedSchools[s] = true
        );

        this.setState(newState);
    }

    private onCloseDialog() {
        this.props.closeDialog();
    }

    private isSelected(schoolCode: string) {
        return this.state.selectedSchools[schoolCode] == true;
    }

    private changeSelected(schoolCode: string) {
        this.setState({
            selectedSchools: {
                ... this.state.selectedSchools,
                [schoolCode]: !this.state.selectedSchools[schoolCode]
            }
        });
    }

    private save() {
        const selected = this.state.selectedSchools;
        const overrides : ChangeSchoolOverrideSpec[] = [];
        this.props.schoolsByCountry.forEach(country => {
            country.schools.forEach(s => {
                const key = getSchoolKey(s);
                if (!selected[key]) return;

                overrides.push({
                    countryCode: country.countryCode,
                    schoolCode: key,
                    schoolName: s.name
                });
            });
        });

        this.props.save(overrides);
    }
}

function mapStateToProps(state: any, ownProps: SchoolOverrideDialogProps) : StateProps {
    return {
        schoolsByCountry: getSchoolsByCountry(state),
        currentSchoolOverrides: getSchoolOverrides(state),
        isDialogOpen: getCurrentDialog(state) == CurrentAgentCommissionDialog.ChangeSchoolOverride
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<any, any, Action>, ownProps: SchoolOverrideDialogProps) : DispatchProps {
    return {
        closeDialog: () => dispatch(changeCurrentDialog(CurrentAgentCommissionDialog.None)),
        save: x => dispatch(changeSchoolOverrides(x))
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(SchoolOverrideDialog);